import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ConfirmDialogComponent, ConfirmDialogData } from '../app/shared/confirm-dialog/confirm-dialog.component';
import { SpsImportListe, SpsListeLoeschErgebnis, SpsListeLoeschvorschau, listenTypInfo } from '../Models/Sps';
import { SpsImportApiService } from './sps-import-api.service';
import { SpsPruefungApiService } from './sps-pruefung-api.service';

/**
 * Gemeinsamer Löschablauf für importierte SPS-Listen – aus der
 * SPS-Listenübersicht und der Projektübersicht gleich:
 *
 *  1. Löschvorschau laden (wie viele Signale bleiben, verschwinden, archiviert werden),
 *  2. Bestätigungsdialog mit Listenname und den Auswirkungen,
 *  3. Löschen über die API, Ergebnis als Snackbar.
 *
 * Liefert das Backend-Ergebnis oder null, wenn der Benutzer abgebrochen hat
 * oder ein Fehler auftrat (die Meldung ist dann bereits angezeigt).
 */
@Injectable({ providedIn: 'root' })
export class SpsListenLoeschenService {

  constructor(
    private api: SpsImportApiService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  loeschen(liste: Pick<SpsImportListe, 'id' | 'name' | 'listenTyp' | 'dateiName' | 'eintraegeAnzahl'>): Observable<SpsListeLoeschErgebnis | null> {
    return this.api.getLoeschvorschau(liste.id).pipe(
      // Ohne Vorschau (z. B. Netzfehler) trotzdem bestätigen lassen – mit den bekannten Kopfdaten.
      catchError(() => of(null)),
      switchMap(vorschau => this.dialog
        .open<ConfirmDialogComponent, ConfirmDialogData, boolean>(ConfirmDialogComponent, {
          data: SpsListenLoeschenService.dialogDaten(liste, vorschau),
          width: '460px',
          maxWidth: '95vw',
          autoFocus: false
        })
        .afterClosed()),
      switchMap(bestaetigt => {
        if (!bestaetigt) {
          return of(null);
        }

        return this.api.deleteListe(liste.id).pipe(
          map(ergebnis => {
            this.snackBar.open(SpsListenLoeschenService.ergebnisText(ergebnis), 'OK', { duration: 5000 });
            return ergebnis;
          }),
          catchError(err => {
            const text = err?.status === 404
              ? `„${liste.name}“ wurde bereits gelöscht oder ist nicht mehr zugänglich.`
              : SpsPruefungApiService.fehlertext(err, `„${liste.name}“ konnte nicht gelöscht werden.`);
            this.snackBar.open(text, 'OK', { duration: 6000 });
            return of(null);
          })
        );
      })
    );
  }

  static dialogDaten(
    liste: Pick<SpsImportListe, 'id' | 'name' | 'listenTyp' | 'dateiName' | 'eintraegeAnzahl'>,
    vorschau: SpsListeLoeschvorschau | null
  ): ConfirmDialogData {
    const typ = listenTypInfo(liste.listenTyp).titel;
    const details: string[] = [
      `${typ} · Datei „${liste.dateiName}“`,
      `${vorschau?.eintraege ?? liste.eintraegeAnzahl} importierte Einträge werden entfernt.`
    ];

    if (vorschau) {
      if (vorschau.signaleMitWeiterenQuellen > 0) {
        details.push(`${vorschau.signaleMitWeiterenQuellen} Signal${vorschau.signaleMitWeiterenQuellen === 1 ? '' : 'e'} `
          + 'kommen auch in anderen Listen vor und bleiben samt Prüfstatus und Verlauf erhalten.');
      }

      if (vorschau.signaleNurDieseListeUngeprueft > 0) {
        details.push(`${vorschau.signaleNurDieseListeUngeprueft} ungeprüfte Signal${vorschau.signaleNurDieseListeUngeprueft === 1 ? '' : 'e'} `
          + 'nur aus dieser Liste werden aus der SPS-Prüfung entfernt.');
      }

      if (vorschau.signaleNurDieseListeGeprueft > 0) {
        details.push(`${vorschau.signaleNurDieseListeGeprueft} geprüfte Signal${vorschau.signaleNurDieseListeGeprueft === 1 ? '' : 'e'} `
          + 'nur aus dieser Liste verschwinden aus der SPS-Prüfung; Prüfstatus und Verlauf bleiben archiviert '
          + 'und werden bei einem erneuten Import wieder zugeordnet.');
      }
    } else {
      details.push('Die Auswirkungen auf die SPS-Signale konnten nicht vorab ermittelt werden.');
    }

    return {
      title: 'SPS-Liste löschen?',
      message: `Die Liste „${liste.name}“ wird aus dem Projekt entfernt.`,
      details,
      warning: 'Dieser Schritt kann nicht rückgängig gemacht werden. Messstellen- und Verbraucherlisten sind nicht betroffen.',
      confirmText: 'Liste löschen'
    };
  }

  static ergebnisText(e: SpsListeLoeschErgebnis): string {
    const teile = [`„${e.name}“ gelöscht.`];

    if (e.signaleErhalten > 0) {
      teile.push(`${e.signaleErhalten} gemeinsame Signale bleiben erhalten.`);
    }

    if (e.signaleGeloescht > 0) {
      teile.push(`${e.signaleGeloescht} Signale entfernt.`);
    }

    if (e.signaleArchiviert > 0) {
      teile.push(`${e.signaleArchiviert} geprüfte Signale archiviert.`);
    }

    return teile.join(' ');
  }
}
